'use client';
import { jsx } from 'react/jsx-runtime';
import { createContext, useState, useRef, useCallback, useEffect, useMemo, useContext, memo, forwardRef, Component } from 'react';

var LoginStatus$1;
(function (LoginStatus) {
    LoginStatus["CONNECTED"] = "connected";
    LoginStatus["AUTHORIZATION_EXPIRED"] = "authorization_expired";
    LoginStatus["NOT_AUTHORIZED"] = "not_authorized";
    LoginStatus["UNKNOWN"] = "unknown";
})(LoginStatus$1 || (LoginStatus$1 = {}));
var LoginStatus = LoginStatus$1;

class FBError extends Error {
    constructor(message, code, type) {
        super(`[react-facebook] ${message} (code: ${code}, type: ${type})`);
        this.name = 'FBError';
        this.code = code;
        this.type = type;
    }
}

var Method;
(function (Method) {
    Method["GET"] = "get";
    Method["POST"] = "post";
    Method["DELETE"] = "delete";
})(Method || (Method = {}));
const defaultOptions = {
    domain: 'connect.facebook.net',
    version: 'v23.0',
    cookie: false,
    status: false,
    xfbml: false,
    language: 'en_US',
    frictionlessRequests: false,
    debug: false,
    chatSupport: false,
    autoLogAppEvents: true,
    lazy: false,
};
function isFBError(response) {
    return typeof response === 'object' && response !== null && 'error' in response;
}
function createFacebook(options) {
    if (!options.appId) {
        throw new Error('[react-facebook] You need to set appId');
    }
    const opts = {
        ...defaultOptions,
        ...options,
    };
    let loadingPromise;
    function getAppId() {
        return opts.appId;
    }
    function getFB() {
        if (typeof window === 'undefined')
            return undefined;
        return window.FB;
    }
    async function init() {
        if (typeof window === 'undefined') {
            return instance;
        }
        if (loadingPromise) {
            return loadingPromise;
        }
        loadingPromise = new Promise((resolve, reject) => {
            const { domain, language, debug, chatSupport, ...restOptions } = opts;
            const initParams = {
                appId: restOptions.appId,
                version: restOptions.version,
                cookie: restOptions.cookie,
                status: restOptions.status,
                xfbml: restOptions.xfbml,
                frictionlessRequests: restOptions.frictionlessRequests,
            };
            let poll;
            const timeout = setTimeout(() => {
                if (poll)
                    clearInterval(poll);
                loadingPromise = undefined;
                instance.loadingPromise = undefined;
                reject(new Error('[react-facebook] Facebook SDK loading timed out after 10 seconds. ' +
                    'This may be caused by an ad blocker or network issue.'));
            }, 10000);
            function onSDKReady() {
                if (poll)
                    clearInterval(poll);
                clearTimeout(timeout);
                window.FB.init(initParams);
                resolve(instance);
            }
            window.fbAsyncInit = onSDKReady;
            if (window.document.getElementById('facebook-jssdk')) {
                if (window.FB) {
                    clearTimeout(timeout);
                    return resolve(instance);
                }
                // Script exists but FB not yet loaded. Keep timeout active and
                // poll as a fallback in case fbAsyncInit already fired before
                // we set our handler (SSR hydration, multiple providers, static script tag).
                poll = setInterval(() => {
                    if (window.FB)
                        onSDKReady();
                }, 50);
                return;
            }
            const js = window.document.createElement('script');
            js.id = 'facebook-jssdk';
            js.async = true;
            js.defer = true;
            js.crossOrigin = 'anonymous';
            js.src = `https://${domain}/${language}/sdk${chatSupport ? '/xfbml.customerchat' : ''}${debug ? '/debug' : ''}.js`;
            js.onerror = () => {
                clearTimeout(timeout);
                loadingPromise = undefined;
                instance.loadingPromise = undefined;
                reject(new Error(`[react-facebook] Failed to load Facebook SDK from ${js.src}. ` +
                    'This may be caused by an ad blocker or network issue.'));
            };
            window.document.body.appendChild(js);
        });
        instance.loadingPromise = loadingPromise;
        return loadingPromise;
    }
    async function call(invoke) {
        await init();
        const fb = getFB();
        if (!fb)
            throw new Error('[react-facebook] Facebook SDK not available');
        return new Promise((resolve, reject) => {
            const callback = (response) => {
                if (!response) {
                    reject(new Error('Response is undefined'));
                }
                else if (isFBError(response)) {
                    const { code, type, message } = response.error;
                    reject(new FBError(message, code, type));
                }
                else {
                    resolve(response);
                }
            };
            invoke(fb, callback);
        });
    }
    async function ui(options) {
        await init();
        const fb = getFB();
        if (!fb)
            throw new Error('[react-facebook] Facebook SDK not available');
        return new Promise((resolve, reject) => {
            fb.ui(options, (response) => {
                if (!response) {
                    resolve(undefined);
                    return;
                }
                if (isFBError(response)) {
                    const { code, type, message } = response.error;
                    reject(new FBError(message, code, type));
                    return;
                }
                resolve(response);
            });
        });
    }
    async function api(path, method = Method.GET, params = {}) {
        return call((fb, cb) => fb.api(path, method, params, cb));
    }
    async function login(loginOpts) {
        var _a;
        const { scope, returnScopes, rerequest, reauthorize } = loginOpts;
        const types = [...((_a = loginOpts.authType) !== null && _a !== void 0 ? _a : [])];
        const fbLoginOptions = { scope };
        if (returnScopes) {
            fbLoginOptions.return_scopes = true;
        }
        if (rerequest) {
            types.push('rerequest');
        }
        if (reauthorize) {
            types.push('reauthenticate');
        }
        if (types.length) {
            fbLoginOptions.auth_type = types.join(',');
        }
        return call((fb, cb) => fb.login(cb, fbLoginOptions));
    }
    async function logout() {
        return call((fb, cb) => fb.logout(cb));
    }
    async function getLoginStatus() {
        return call((fb, cb) => fb.getLoginStatus(cb));
    }
    async function getTokenDetail(loginResponse) {
        if ((loginResponse === null || loginResponse === void 0 ? void 0 : loginResponse.status) === LoginStatus.CONNECTED) {
            return loginResponse.authResponse;
        }
        const response = await getLoginStatus();
        if (response.status === LoginStatus.CONNECTED) {
            return response.authResponse;
        }
        throw new Error('Token is undefined');
    }
    async function getProfile(params) {
        return api('/me', Method.GET, params);
    }
    async function getPermissions() {
        const response = await api('/me/permissions');
        return response.data;
    }
    async function hasPermissions(permissions) {
        const usersPermissions = await getPermissions();
        const matchedPermissions = permissions.filter((p) => {
            return usersPermissions.some((row) => row.status === 'granted' && row.permission === p);
        });
        return matchedPermissions.length === permissions.length;
    }
    async function subscribe(eventName, callback) {
        await init();
        const fb = getFB();
        if (!fb)
            throw new Error('[react-facebook] Facebook SDK not available');
        fb.Event.subscribe(eventName, callback);
        return () => unsubscribe(eventName, callback);
    }
    async function unsubscribe(eventName, callback) {
        await init();
        const fb = getFB();
        if (!fb)
            throw new Error('[react-facebook] Facebook SDK not available');
        fb.Event.unsubscribe(eventName, callback);
    }
    async function parse(parentNode) {
        await init();
        const fb = getFB();
        if (!fb)
            throw new Error('[react-facebook] Facebook SDK not available');
        if (parentNode) {
            fb.XFBML.parse(parentNode);
        }
        else {
            fb.XFBML.parse();
        }
    }
    function getLocale() {
        return opts.language || 'en_US';
    }
    async function changeLocale(newLocale) {
        var _a;
        if (typeof window === 'undefined')
            return;
        opts.language = newLocale;
        const existingScript = window.document.getElementById('facebook-jssdk');
        if (existingScript) {
            existingScript.remove();
        }
        if (window.FB) {
            delete window.FB;
        }
        loadingPromise = undefined;
        instance.loadingPromise = undefined;
        await init();
        const fb = getFB();
        if ((_a = fb === null || fb === void 0 ? void 0 : fb.XFBML) === null || _a === void 0 ? void 0 : _a.parse) {
            fb.XFBML.parse();
        }
    }
    const instance = {
        getAppId,
        getFB,
        init,
        ui,
        api,
        login,
        logout,
        getLoginStatus,
        getTokenDetail,
        getProfile,
        getPermissions,
        hasPermissions,
        subscribe,
        unsubscribe,
        parse,
        getLocale,
        changeLocale,
        options: opts,
        loadingPromise: undefined,
    };
    if (!opts.lazy && typeof window !== 'undefined') {
        init();
    }
    return instance;
}

var FacebookContext = createContext(undefined);

const defaultPixelOptions = {
    autoConfig: true,
    debug: false,
    advancedMatching: {},
};
function createFacebookPixel(options) {
    var _a;
    if (!options.pixelId) {
        throw new Error('You need to set pixelId');
    }
    const opts = {
        ...defaultPixelOptions,
        ...options,
        debug: (_a = options.debug) !== null && _a !== void 0 ? _a : false,
    };
    let loadingPromise;
    function warn(...args) {
        if (!opts.debug)
            return;
        console.warn('[react-facebook-pixel]', ...args);
    }
    function log(...args) {
        if (!opts.debug)
            return;
        console.info('[react-facebook-pixel]', ...args);
    }
    function loadPixelScript() {
        return new Promise((resolve, reject) => {
            if (typeof window === 'undefined') {
                resolve();
                return;
            }
            if (window.fbq) {
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.async = true;
            script.src = 'https://connect.facebook.net/en_US/fbevents.js';
            script.onload = () => {
                if (!window.fbq) {
                    // Real fbevents.js failed to set up fbq; create a stub
                    const fbq = function (...args) {
                        if (fbq.callMethod) {
                            fbq.callMethod(...args);
                        }
                        else {
                            fbq.queue.push(args);
                        }
                    };
                    fbq.queue = [];
                    fbq.loaded = true;
                    fbq.version = '2.0';
                    fbq.push = fbq;
                    window.fbq = fbq;
                }
                if (!window._fbq) {
                    window._fbq = window.fbq;
                }
                resolve();
            };
            script.onerror = () => {
                warn('Failed to load Facebook Pixel script');
                reject(new Error('[react-facebook] Failed to load Facebook Pixel script. ' +
                    'This may be caused by an ad blocker or network issue.'));
            };
            document.head.appendChild(script);
        });
    }
    async function init() {
        if (loadingPromise) {
            return loadingPromise;
        }
        loadingPromise = loadPixelScript()
            .then(() => {
            if (typeof window === 'undefined' || !window.fbq)
                return instance;
            const { pixelId, autoConfig, advancedMatching } = opts;
            if (autoConfig === false) {
                window.fbq('set', 'autoConfig', false, pixelId);
            }
            window.fbq('init', pixelId, advancedMatching);
            log('Pixel initialized successfully');
            return instance;
        })
            .catch((error) => {
            loadingPromise = undefined;
            throw error;
        });
        return loadingPromise;
    }
    function getFbq() {
        const fbq = window.fbq;
        if (!fbq)
            throw new Error('[react-facebook] Facebook Pixel not initialized');
        return fbq;
    }
    async function pageView() {
        await init();
        getFbq()('track', 'PageView');
        log("Called fbq('track', 'PageView')");
    }
    async function track(eventName, data) {
        await init();
        getFbq()('track', eventName, data);
        log(`Called fbq('track', '${eventName}')`);
        if (data)
            log('with data:', data);
    }
    async function trackSingle(pixelId, eventName, data) {
        await init();
        getFbq()('trackSingle', pixelId, eventName, data);
        log(`Called fbq('trackSingle', '${pixelId}', '${eventName}')`);
        if (data)
            log('with data:', data);
    }
    async function trackCustom(eventName, data) {
        await init();
        getFbq()('trackCustom', eventName, data);
        log(`Called fbq('trackCustom', '${eventName}')`);
        if (data)
            log('with data:', data);
    }
    async function trackSingleCustom(pixelId, eventName, data) {
        await init();
        getFbq()('trackSingleCustom', pixelId, eventName, data);
        log(`Called fbq('trackSingleCustom', '${pixelId}', '${eventName}')`);
        if (data)
            log('with data:', data);
    }
    async function grantConsent() {
        await init();
        getFbq()('consent', 'grant');
        log("Called fbq('consent', 'grant')");
    }
    async function revokeConsent() {
        await init();
        getFbq()('consent', 'revoke');
        log("Called fbq('consent', 'revoke')");
    }
    async function fbqDirect(...args) {
        await init();
        getFbq()(...args);
        log(`Called fbq('${args.slice(0, 2).join("', '")}')`);
        if (args[2])
            log('with data:', args[2]);
    }
    function isInitialized() {
        return !!loadingPromise;
    }
    function getOptions() {
        return { ...opts };
    }
    const instance = {
        init,
        pageView,
        track,
        trackSingle,
        trackCustom,
        trackSingleCustom,
        grantConsent,
        revokeConsent,
        fbq: fbqDirect,
        isInitialized,
        getOptions,
    };
    return instance;
}

const FacebookPixelContext = createContext(undefined);
function FacebookPixelProvider(props) {
    const { children, lazy = false, ...options } = props;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(undefined);
    const pixelRef = useRef(undefined);
    const initPromiseRef = useRef(undefined);
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const init = useCallback(async () => {
        if (initPromiseRef.current) {
            return initPromiseRef.current;
        }
        initPromiseRef.current = (async () => {
            try {
                if (pixelRef.current) {
                    return;
                }
                setLoading(true);
                setError(undefined);
                pixelRef.current = createFacebookPixel(optionsRef.current);
                await pixelRef.current.init();
            }
            catch (error) {
                setError(error instanceof Error ? error : new Error(String(error)));
            }
            finally {
                setLoading(false);
            }
        })();
        return initPromiseRef.current;
    }, []);
    const pageView = useCallback(async () => {
        var _a;
        await init();
        await ((_a = pixelRef.current) === null || _a === void 0 ? void 0 : _a.pageView());
    }, [init]);
    const track = useCallback(async (eventName, data) => {
        var _a;
        await init();
        await ((_a = pixelRef.current) === null || _a === void 0 ? void 0 : _a.track(eventName, data));
    }, [init]);
    const trackCustom = useCallback(async (eventName, data) => {
        var _a;
        await init();
        await ((_a = pixelRef.current) === null || _a === void 0 ? void 0 : _a.trackCustom(eventName, data));
    }, [init]);
    const grantConsent = useCallback(async () => {
        var _a;
        await init();
        await ((_a = pixelRef.current) === null || _a === void 0 ? void 0 : _a.grantConsent());
    }, [init]);
    const revokeConsent = useCallback(async () => {
        var _a;
        await init();
        await ((_a = pixelRef.current) === null || _a === void 0 ? void 0 : _a.revokeConsent());
    }, [init]);
    const fbq = useCallback(async (...args) => {
        var _a;
        await init();
        await ((_a = pixelRef.current) === null || _a === void 0 ? void 0 : _a.fbq(...args));
    }, [init]);
    useEffect(() => {
        if (!lazy) {
            init();
        }
    }, [lazy]);
    const value = useMemo(() => ({
        loading,
        error,
        init,
        pixel: pixelRef.current,
        pageView,
        track,
        trackCustom,
        grantConsent,
        revokeConsent,
        fbq,
    }), [loading, error, init, pageView, track, trackCustom, grantConsent, revokeConsent, fbq]);
    return jsx(FacebookPixelContext.Provider, { value: value, children: children });
}

function FacebookProvider(props) {
    const { children, pixelId, ...options } = props;
    const [loading, setLoading] = useState(true);
    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState();
    const [locale, setLocaleState] = useState(options.language || 'en_US');
    const apiRef = useRef(undefined);
    const initPromiseRef = useRef(undefined);
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const init = useCallback(async () => {
        if (initPromiseRef.current) {
            return initPromiseRef.current;
        }
        initPromiseRef.current = (async () => {
            try {
                if (apiRef.current) {
                    return apiRef.current;
                }
                setIsReady(false);
                setLoading(true);
                apiRef.current = createFacebook(optionsRef.current);
                await apiRef.current.init();
                setIsReady(true);
                return apiRef.current;
            }
            catch (error) {
                setError(error instanceof Error ? error : new Error(String(error)));
            }
            finally {
                setLoading(false);
            }
            return apiRef.current;
        })();
        return initPromiseRef.current;
    }, []);
    const parse = useCallback(async (element) => {
        const api = await init();
        if (api) {
            await api.parse(element);
        }
    }, [init]);
    const setLocale = useCallback(async (newLocale) => {
        if (!apiRef.current) {
            setLocaleState(newLocale);
            return;
        }
        try {
            setLoading(true);
            setError(undefined);
            await apiRef.current.changeLocale(newLocale);
            setLocaleState(newLocale);
        }
        catch (error) {
            setError(error instanceof Error ? error : new Error(String(error)));
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        const { lazy } = options;
        if (!lazy) {
            init();
        }
    }, [options.lazy]);
    // Sync locale when the language *prop* changes (controlled mode).
    // We intentionally exclude `locale` from the dependency array so that
    // programmatic setLocale() calls are not reverted.
    useEffect(() => {
        const newLocale = options.language || 'en_US';
        if (newLocale !== locale) {
            setLocale(newLocale);
        }
    }, [options.language]);
    const value = useMemo(() => ({
        loading,
        error,
        init,
        api: isReady ? apiRef.current : undefined,
        parse,
        locale,
        setLocale,
    }), [loading, error, init, isReady, parse, locale, setLocale]);
    const content = jsx(FacebookContext.Provider, { value: value, children: children });
    if (pixelId) {
        return jsx(FacebookPixelProvider, { pixelId: pixelId, children: content });
    }
    return content;
}

/**
 * Hook for accessing the Facebook SDK context
 *
 * @returns The Facebook context with API instance, loading state, and utilities
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { api, loading, error } = useFacebook();
 *
 *   if (loading) return <p>Loading Facebook SDK...</p>;
 *   if (error) return <p>Error: {error.message}</p>;
 *
 *   return <p>Facebook SDK ready! App ID: {api?.getAppId()}</p>;
 * }
 * ```
 */
function useFacebook(props = {}) {
    const { lazy = false } = props;
    const context = useContext(FacebookContext);
    if (!context) {
        throw new Error('[react-facebook] useFacebook must be used within a <FacebookProvider>. ' +
            'Wrap your component tree with <FacebookProvider appId="YOUR_APP_ID">.');
    }
    useEffect(() => {
        if (!lazy) {
            context.init();
        }
    }, [lazy, context.init]);
    return context;
}

function Parser$1(props, forwardedRef) {
    const { as: As = 'div', children, className, style, data } = props;
    const { parse } = useFacebook();
    const [element, setElement] = useState(null);
    const handleRef = useCallback((element) => {
        setElement(element);
        if (forwardedRef) {
            if (typeof forwardedRef === 'function') {
                forwardedRef(element);
            }
            else if (forwardedRef) {
                forwardedRef.current = element;
            }
        }
    }, [forwardedRef]);
    const key = useMemo(() => {
        let uniqueKey = `fb-parser-${As}-${className}`;
        if (!data) {
            return uniqueKey;
        }
        for (const key in data) {
            const value = data[key];
            if (value !== undefined) {
                uniqueKey += `-${key}:${data[key] || ''}`;
            }
        }
        return uniqueKey;
    }, [data, As, className]);
    useEffect(() => {
        if (element === null || element === void 0 ? void 0 : element.parentNode) {
            parse(element.parentNode);
        }
    }, [element, parse, key]);
    return (jsx(As, { className: className, style: style, ref: handleRef, ...data, children: children }));
}
var Parser = memo(forwardRef(Parser$1));

const canUseDOM = !!(typeof window !== 'undefined' && window.document && window.document.createElement);
function getCurrentHref() {
    if (!canUseDOM) {
        return 'https://www.facebook.com';
    }
    return window.location.href;
}

function Like(props, ref) {
    const { className = '', href = getCurrentHref(), layout, colorScheme, action, showFaces, share, width, size, kidDirectedSite, referral, lazy, ...rest } = props;
    const data = useMemo(() => {
        return {
            'data-ref': referral,
            'data-href': href,
            'data-layout': layout,
            'data-colorscheme': colorScheme,
            'data-action': action,
            'data-show-faces': showFaces ? 'true' : 'false',
            'data-share': share ? 'true' : 'false',
            'data-width': width === null || width === void 0 ? void 0 : width.toString(),
            'data-size': size,
            'data-lazy': lazy ? 'true' : 'false',
            'data-kid-directed-site': kidDirectedSite ? 'true' : 'false',
        };
    }, [referral, href, layout, colorScheme, action, showFaces, share, width, size, kidDirectedSite, lazy]);
    return jsx(Parser, { className: `fb-like ${className}`, data: data, ...rest, ref: ref });
}
var Like_default = memo(forwardRef(Like));

function clearUndefinedProperties(obj) {
    return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined));
}

/**
 * Internal factory hook for Facebook dialog-based actions (share, feed, send).
 * Not exported to consumers.
 */
function useDialog(method, buildParams) {
    const { init } = useFacebook();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined);
    async function invoke(options) {
        try {
            setError(undefined);
            setLoading(true);
            const api = await init();
            if (!api) {
                throw new Error('[react-facebook] Facebook API is not initialized');
            }
            return api.ui(clearUndefinedProperties({
                method,
                ...buildParams(options, api),
            }));
        }
        catch (error) {
            const err = error instanceof Error ? error : new Error(String(error));
            setError(err);
            throw err;
        }
        finally {
            setLoading(false);
        }
    }
    return { loading, error, invoke };
}

/**
 * Hook for sharing content via Facebook Share dialog
 *
 * @returns Object with share function, loading state, and error
 *
 * @example
 * ```tsx
 * function ShareComponent() {
 *   const { share, loading, error } = useShare();
 *
 *   const handleShare = () => {
 *     share({
 *       href: 'https://example.com',
 *       display: 'popup',
 *       hashtag: '#example',
 *     });
 *   };
 *
 *   return <button onClick={handleShare} disabled={loading}>Share</button>;
 * }
 * ```
 */
function useShare() {
    const { loading, error, invoke } = useDialog('share', (options, api) => {
        const { href, display, hashtag, redirectUri, ...rest } = options;
        return { href, display, app_id: api.getAppId(), hashtag, redirect_uri: redirectUri, ...rest };
    });
    return { loading, error, share: invoke };
}

function ShareButton(props) {
    const { as: AsChild = 'button', disabled, href, display, hashtag, redirectUri, ...rest } = props;
    const { loading, share } = useShare();
    const isDisabled = disabled || loading;
    function handleShare() {
        if (isDisabled)
            return;
        share({
            href,
            display,
            hashtag,
            redirectUri,
        });
    }
    return jsx(AsChild, { onClick: handleShare, disabled: isDisabled, ...rest });
}

function Page(props, ref) {
    const { className = '', style, href = getCurrentHref(), tabs, hideCover, width, height, showFacepile, hideCTA, smallHeader, adaptContainerWidth, lazy, ...rest } = props;
    const data = useMemo(() => {
        return {
            'data-tabs': tabs,
            'data-hide-cover': hideCover ? 'true' : 'false',
            'data-show-facepile': showFacepile ? 'true' : 'false',
            'data-hide-cta': hideCTA ? 'true' : 'false',
            'data-href': href,
            'data-small-header': smallHeader ? 'true' : 'false',
            'data-adapt-container-width': adaptContainerWidth ? 'true' : 'false',
            'data-height': height === null || height === void 0 ? void 0 : height.toString(),
            'data-width': width === null || width === void 0 ? void 0 : width.toString(),
            'data-lazy': lazy ? 'true' : 'false',
        };
    }, [href, tabs, hideCover, width, height, showFacepile, hideCTA, smallHeader, adaptContainerWidth, lazy]);
    return jsx(Parser, { className: `fb-page ${className}`, style: style, data: data, ...rest, ref: ref });
}
var Page_default = memo(forwardRef(Page));

/**
 * Hook for handling Facebook login and logout
 *
 * @returns Object with login/logout functions, loading state, error, and login status
 *
 * @example
 * ```tsx
 * function LoginComponent() {
 *   const { login, logout, loading, error, status } = useLogin();
 *
 *   if (status === 'connected') {
 *     return <button onClick={logout} disabled={loading}>Logout</button>;
 *   }
 *
 *   return <button onClick={() => login({ scope: 'email' })} disabled={loading}>Login</button>;
 * }
 * ```
 */
function useLogin() {
    const { api, loading: sdkLoading, init } = useFacebook();
    const [error, setError] = useState(undefined);
    const [actionLoading, setActionLoading] = useState(false);
    const [latestResponse, setLatestResponse] = useState();
    const apiRef = useRef(api);
    apiRef.current = api;
    const handleLogin = useCallback(async (loginOptions, callback) => {
        try {
            if (!apiRef.current) {
                throw new Error('[react-facebook] Facebook API is not initialized');
            }
            setError(undefined);
            setActionLoading(true);
            const response = await apiRef.current.login(loginOptions);
            if (response.status !== LoginStatus.CONNECTED) {
                throw new Error('[react-facebook] Unauthorized user');
            }
            setLatestResponse(response);
            callback === null || callback === void 0 ? void 0 : callback(response);
            return response;
        }
        catch (error) {
            const err = error instanceof Error ? error : new Error(String(error));
            setError(err);
            throw err;
        }
        finally {
            setActionLoading(false);
        }
    }, []);
    const handleLogout = useCallback(async () => {
        try {
            setError(undefined);
            setActionLoading(true);
            const api = await init();
            if (!api) {
                throw new Error('[react-facebook] Facebook API is not initialized');
            }
            await api.logout();
            setLatestResponse(undefined);
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            throw error;
        }
        finally {
            setActionLoading(false);
        }
    }, [init]);
    return {
        login: handleLogin,
        logout: handleLogout,
        loading: sdkLoading || actionLoading,
        error,
        status: latestResponse === null || latestResponse === void 0 ? void 0 : latestResponse.status,
    };
}

/**
 * Hook for subscribing to Facebook SDK events
 *
 * @param event - The Facebook event name to subscribe to (e.g., 'auth.statusChange')
 * @param callback - Optional callback invoked when the event fires
 * @returns The last event value received, or undefined
 *
 * @example
 * ```tsx
 * function AuthListener() {
 *   const lastStatus = useSubscribe('auth.statusChange', (response) => {
 *     console.log('Auth status changed:', response.status);
 *   });
 *
 *   return <p>Last status: {JSON.stringify(lastStatus)}</p>;
 * }
 * ```
 */
function useSubscribe(event, callback) {
    const [lastValue, setLastValue] = useState(undefined);
    const { init } = useFacebook();
    const callbackRef = useRef(callback);
    callbackRef.current = callback;
    const handleResponse = useCallback((value) => {
        var _a;
        setLastValue(value);
        (_a = callbackRef.current) === null || _a === void 0 ? void 0 : _a.call(callbackRef, value);
    }, []);
    useEffect(() => {
        let cancelled = false;
        let unsubscribe;
        (async () => {
            const api = await init();
            if (cancelled)
                return;
            if (api) {
                unsubscribe = await api.subscribe(event, handleResponse);
                if (cancelled)
                    unsubscribe();
            }
        })();
        return () => {
            cancelled = true;
            unsubscribe === null || unsubscribe === void 0 ? void 0 : unsubscribe();
        };
    }, [event, handleResponse, init]);
    return lastValue;
}

/**
 * Hook for monitoring the user's Facebook login status
 *
 * @returns Object with current login status, loading state, and error
 *
 * @example
 * ```tsx
 * function StatusComponent() {
 *   const { status, loading } = useLoginStatus();
 *
 *   if (loading) return <p>Checking status...</p>;
 *
 *   return <p>Status: {status}</p>;
 * }
 * ```
 */
function useLoginStatus() {
    const { init } = useFacebook();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(undefined);
    const [status, setStatus] = useState(LoginStatus.UNKNOWN);
    const handleStatusChanges = useCallback((response) => {
        setStatus(response.status);
    }, []);
    useSubscribe('auth.statusChange', handleStatusChanges);
    useEffect(() => {
        let cancelled = false;
        async function fetchStatus() {
            try {
                setLoading(true);
                const api = await init();
                if (cancelled)
                    return;
                if (!api) {
                    throw new Error('Facebook API is not initialized');
                }
                const { status } = await api.getLoginStatus();
                if (!cancelled)
                    setStatus(status);
            }
            catch (error) {
                if (!cancelled) {
                    setError(error instanceof Error ? error : new Error(String(error)));
                }
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        }
        fetchStatus();
        return () => {
            cancelled = true;
        };
    }, [init]);
    return {
        loading,
        error,
        status,
    };
}

/**
 * Hook for fetching the logged-in user's Facebook profile
 *
 * @param fields - Array of profile fields to request (e.g., ['name', 'email', 'picture'])
 * @returns Object with profile data, loading state, and error
 *
 * @example
 * ```tsx
 * function ProfileComponent() {
 *   const { profile, loading, error } = useProfile(['name', 'email', 'picture']);
 *
 *   if (loading) return <p>Loading profile...</p>;
 *   if (error) return <p>Error: {error.message}</p>;
 *   if (!profile) return <p>Not logged in</p>;
 *
 *   return <p>Hello, {profile.name}!</p>;
 * }
 * ```
 */
function useProfile(fields, enabled = true) {
    const { init } = useFacebook();
    const { status } = useLoginStatus();
    const [loading, setLoading] = useState(enabled);
    const [profile, setProfile] = useState(undefined);
    const [error, setError] = useState(undefined);
    // Stabilize fields reference to prevent infinite re-renders
    const fieldsKey = fields.join(',');
    useEffect(() => {
        if (!enabled) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        async function updateProfile() {
            try {
                setError(undefined);
                setLoading(true);
                const api = await init();
                if (!api || cancelled)
                    return;
                if (status === LoginStatus.CONNECTED) {
                    const result = await api.getProfile({
                        fields: fieldsKey,
                    });
                    if (!cancelled) {
                        setProfile(result);
                    }
                }
                else {
                    if (!cancelled) {
                        setProfile(undefined);
                    }
                }
            }
            catch (error) {
                if (!cancelled) {
                    setError(error instanceof Error ? error : new Error(String(error)));
                }
            }
            finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }
        updateProfile();
        return () => {
            cancelled = true;
        };
    }, [enabled, status, fieldsKey, init]);
    return {
        loading,
        error,
        profile,
    };
}

function Login(props) {
    const { children, onSuccess, onError, onProfileSuccess, fields = [], as: Component = 'button', disabled = false, scope = ['public_profile', 'email'], returnScopes, authType, rerequest, reauthorize, ...rest } = props;
    const { loading, login } = useLogin();
    const shouldFetchProfile = !!onProfileSuccess && fields.length > 0;
    const { profile } = useProfile(fields, shouldFetchProfile);
    const onProfileSuccessRef = useRef(onProfileSuccess);
    onProfileSuccessRef.current = onProfileSuccess;
    useEffect(() => {
        if (profile && onProfileSuccessRef.current) {
            onProfileSuccessRef.current(profile);
        }
    }, [profile]);
    const handleLogin = async () => {
        if (loading || disabled)
            return;
        try {
            const response = await login({
                scope: Array.isArray(scope) ? scope.join(',') : scope,
                returnScopes,
                authType,
                rerequest,
                reauthorize,
            });
            onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(response);
        }
        catch (error) {
            onError === null || onError === void 0 ? void 0 : onError(error instanceof Error ? error : new Error(String(error)));
        }
    };
    const isDisabled = disabled || loading;
    // Render props pattern
    if (typeof children === 'function') {
        return children({
            onClick: handleLogin,
            loading,
            isDisabled,
        });
    }
    // Default button rendering
    return (jsx(Component, { onClick: handleLogin, disabled: isDisabled, "aria-busy": loading, "aria-label": loading ? 'Logging in...' : 'Login with Facebook', ...rest, children: loading ? 'Loading...' : children || 'Login with Facebook' }));
}

function EmbeddedPost(props, ref) {
    const { className = '', href, width, showText, lazy, ...rest } = props;
    const data = useMemo(() => {
        return {
            'data-href': href,
            'data-width': width === null || width === void 0 ? void 0 : width.toString(),
            'data-lazy': lazy ? 'true' : 'false',
            'data-show-text': showText ? 'true' : 'false',
        };
    }, [href, width, lazy, showText]);
    return jsx(Parser, { className: `fb-post ${className}`, data: data, ...rest, ref: ref });
}
var EmbeddedPost_default = memo(forwardRef(EmbeddedPost));

function EmbeddedVideo(props, ref) {
    const { className = '', href, width, showText, allowFullScreen, autoPlay, lazy, showCaptions, ...rest } = props;
    const data = useMemo(() => {
        return {
            'data-href': href,
            'data-width': width === null || width === void 0 ? void 0 : width.toString(),
            'data-show-text': showText ? 'true' : 'false',
            'data-show-captions': showCaptions ? 'true' : 'false',
            'data-autoplay': autoPlay ? 'true' : 'false',
            'data-lazy': lazy ? 'true' : 'false',
            'data-allowfullscreen': allowFullScreen ? 'true' : 'false',
        };
    }, [href, width, showText, showCaptions, autoPlay, lazy, allowFullScreen]);
    return jsx(Parser, { className: `fb-video ${className}`, data: data, ...rest, ref: ref });
}
var EmbeddedVideo_default = memo(forwardRef(EmbeddedVideo));

function Comments(props, ref) {
    const { className = '', colorScheme, href = getCurrentHref(), numPosts, orderBy, width, mobile, lazy, ...rest } = props;
    const data = useMemo(() => {
        return {
            'data-colorscheme': colorScheme,
            'data-numposts': numPosts === null || numPosts === void 0 ? void 0 : numPosts.toString(),
            'data-href': href,
            'data-order-by': orderBy,
            'data-width': width === null || width === void 0 ? void 0 : width.toString(),
            'data-skin': colorScheme,
            'data-mobile': mobile ? 'true' : 'false',
            'data-lazy': lazy ? 'true' : 'false',
        };
    }, [colorScheme, href, numPosts, orderBy, width, mobile, lazy]);
    return jsx(Parser, { className: `fb-comments ${className}`, data: data, ...rest, ref: ref });
}
var Comments_default = memo(forwardRef(Comments));

function CommentsCount(props, ref) {
    const { className = '', href = getCurrentHref(), ...rest } = props;
    const data = useMemo(() => {
        return {
            'data-href': href,
        };
    }, [href]);
    return jsx(Parser, { as: "span", className: `fb-comments-count ${className}`, data: data, ...rest, ref: ref });
}
var CommentsCount_default = memo(forwardRef(CommentsCount));

function Share(props, ref) {
    const { className = '', href = getCurrentHref(), lazy, layout, size, ...rest } = props;
    const data = useMemo(() => {
        return {
            'data-href': href,
            'data-lazy': lazy ? 'true' : 'false',
            'data-size': size,
            'data-layout': layout,
        };
    }, [href, lazy, size, layout]);
    return jsx(Parser, { className: `fb-share-button ${className}`, data: data, ...rest, ref: ref });
}
var Share_default = memo(forwardRef(Share));

/**
 * Error boundary component for gracefully handling Facebook SDK errors.
 * Catches errors from ad blockers, network failures, and SDK initialization issues.
 *
 * @example
 * ```tsx
 * <FacebookErrorBoundary
 *   fallback={(error, reset) => (
 *     <div>
 *       <p>Facebook features unavailable: {error.message}</p>
 *       <button onClick={reset}>Retry</button>
 *     </div>
 *   )}
 * >
 *   <FacebookProvider appId="YOUR_APP_ID">
 *     <App />
 *   </FacebookProvider>
 * </FacebookErrorBoundary>
 * ```
 */
class FacebookErrorBoundary extends Component {
    constructor() {
        super(...arguments);
        this.state = { hasError: false, error: null };
        this.reset = () => {
            this.setState({ hasError: false, error: null });
        };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        var _a, _b;
        (_b = (_a = this.props).onError) === null || _b === void 0 ? void 0 : _b.call(_a, error, errorInfo);
    }
    render() {
        if (this.state.hasError && this.state.error) {
            const { fallback } = this.props;
            if (typeof fallback === 'function') {
                return fallback(this.state.error, this.reset);
            }
            if (fallback) {
                return fallback;
            }
            return null;
        }
        return this.props.children;
    }
}

var LikeSize;
(function (LikeSize) {
    LikeSize["SMALL"] = "small";
    LikeSize["LARGE"] = "large";
})(LikeSize || (LikeSize = {}));
var LikeSize_default = LikeSize;

var LikeLayout;
(function (LikeLayout) {
    LikeLayout["STANDARD"] = "standard";
    LikeLayout["BUTTON_COUNT"] = "button_count";
    LikeLayout["BUTTON"] = "button";
    LikeLayout["BOX_COUNT"] = "box_count";
})(LikeLayout || (LikeLayout = {}));
var LikeLayout_default = LikeLayout;

var ColorScheme;
(function (ColorScheme) {
    ColorScheme["LIGHT"] = "light";
    ColorScheme["DARK"] = "dark";
})(ColorScheme || (ColorScheme = {}));
var ColorScheme_default = ColorScheme;

var LikeAction;
(function (LikeAction) {
    LikeAction["LIKE"] = "like";
    LikeAction["RECOMMEND"] = "recommend";
})(LikeAction || (LikeAction = {}));
var LikeAction_default = LikeAction;

var CommentsOrderBy;
(function (CommentsOrderBy) {
    CommentsOrderBy["SOCIAL"] = "social";
    CommentsOrderBy["REVERSE_TIME"] = "reverse_time";
    CommentsOrderBy["TIME"] = "time";
})(CommentsOrderBy || (CommentsOrderBy = {}));
var CommentsOrderBy_default = CommentsOrderBy;

var MessengerSize;
(function (MessengerSize) {
    MessengerSize["SMALL"] = "small";
    MessengerSize["MEDIUM"] = "medium";
    MessengerSize["STANDARD"] = "standard";
    MessengerSize["LARGE"] = "large";
    MessengerSize["XLARGE"] = "xlarge";
})(MessengerSize || (MessengerSize = {}));
var MessengerSize_default = MessengerSize;

var MessengerColor;
(function (MessengerColor) {
    MessengerColor["BLUE"] = "blue";
    MessengerColor["WHITE"] = "white";
})(MessengerColor || (MessengerColor = {}));
var MessengerColor_default = MessengerColor;

// @flow
// 3.0 fields without app review https://developers.facebook.com/docs/facebook-login/permissions/?locale=en_US#reference-default
var Fields = [
    'id',
    'first_name',
    'last_name',
    'middle_name',
    'name',
    'name_format',
    'picture',
    'short_name',
    'email',
];

/**
 * Hook for managing Facebook SDK locale dynamically
 *
 * @returns Object with current locale, setLocale function, and loading state
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { locale, setLocale, isChangingLocale } = useLocale();
 *
 *   return (
 *     <div>
 *       <p>Current locale: {locale}</p>
 *       <button
 *         onClick={() => setLocale('es_ES')}
 *         disabled={isChangingLocale}
 *       >
 *         Switch to Spanish
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
function useLocale() {
    const { locale, setLocale: setFacebookLocale, loading } = useFacebook();
    const [isChangingLocale, setIsChangingLocale] = useState(false);
    const setLocale = async (newLocale) => {
        if (newLocale === locale) {
            return;
        }
        try {
            setIsChangingLocale(true);
            await setFacebookLocale(newLocale);
        }
        finally {
            setIsChangingLocale(false);
        }
    };
    return {
        locale,
        setLocale,
        isChangingLocale: isChangingLocale || loading,
    };
}

/**
 * Hook for posting to a user's feed via Facebook Feed dialog
 *
 * @returns Object with feed function, loading state, and error
 *
 * @example
 * ```tsx
 * function FeedComponent() {
 *   const { feed, loading } = useFeed();
 *
 *   const handlePost = () => {
 *     feed({
 *       from: 'me',
 *       to: 'friend_id',
 *       link: 'https://example.com',
 *       name: 'Check this out!',
 *     });
 *   };
 *
 *   return <button onClick={handlePost} disabled={loading}>Post</button>;
 * }
 * ```
 */
function useFeed() {
    const { loading, error, invoke } = useDialog('feed', (options, api) => {
        const { link = getCurrentHref(), appId = api.getAppId(), display, redirectURI, from, to, picture, source, name, caption, description, dataRef, } = options;
        return {
            link,
            display,
            app_id: appId,
            redirect_uri: redirectURI,
            from,
            to,
            picture,
            source,
            name,
            caption,
            description,
            ref: dataRef,
        };
    });
    return { loading, error, feed: invoke };
}

/**
 * Hook for sending messages via Facebook Send dialog
 *
 * @returns Object with send function, loading state, and error
 *
 * @example
 * ```tsx
 * function SendComponent() {
 *   const { send, loading } = useSend();
 *
 *   const handleSend = () => {
 *     send({
 *       to: 'friend_id',
 *       link: 'https://example.com',
 *     });
 *   };
 *
 *   return <button onClick={handleSend} disabled={loading}>Send</button>;
 * }
 * ```
 */
function useSend() {
    const { loading, error, invoke } = useDialog('send', (options, api) => {
        const { link = getCurrentHref(), display, appId = api.getAppId(), to, redirectURI } = options;
        return {
            link,
            display,
            app_id: appId,
            to,
            redirect_uri: redirectURI,
        };
    });
    return { loading, error, send: invoke };
}

/**
 * Hook for accessing Facebook Pixel tracking functionality
 *
 * @returns The Pixel context with tracking methods, loading state, and error
 *
 * @example
 * ```tsx
 * function TrackButton() {
 *   const { track, loading } = usePixel();
 *
 *   return (
 *     <button onClick={() => track('Purchase', { value: 9.99, currency: 'USD' })}>
 *       Buy Now
 *     </button>
 *   );
 * }
 * ```
 */
function usePixel(props = {}) {
    const { lazy = false } = props;
    const context = useContext(FacebookPixelContext);
    if (!context) {
        throw new Error('[react-facebook] usePixel must be used within a <FacebookPixelProvider>. ' +
            'Wrap your component tree with <FacebookPixelProvider pixelId="YOUR_PIXEL_ID"> ' +
            'or use the pixel prop on <FacebookProvider>.');
    }
    useEffect(() => {
        if (!lazy) {
            context.init();
        }
    }, [lazy, context.init]);
    return context;
}

// Shared history patch: only applied once, dispatches a custom event
// that all hook instances can listen to without interfering with each other.
const HISTORY_CHANGE_EVENT = 'react-facebook:history-change';
let historyPatched = false;
function ensureHistoryPatched() {
    if (historyPatched || typeof window === 'undefined')
        return;
    historyPatched = true;
    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);
    history.pushState = (...args) => {
        originalPushState(...args);
        window.dispatchEvent(new Event(HISTORY_CHANGE_EVENT));
    };
    history.replaceState = (...args) => {
        originalReplaceState(...args);
        window.dispatchEvent(new Event(HISTORY_CHANGE_EVENT));
    };
}
/**
 * Hook for automatically tracking Facebook Pixel page views
 *
 * @param props - Configuration for page view tracking
 * @returns Object with manual pageView trigger and loading state
 *
 * @example
 * ```tsx
 * function MyPage() {
 *   // Automatically tracks page view on mount
 *   usePageView();
 *
 *   return <div>Page content</div>;
 * }
 *
 * // With route change tracking
 * function App() {
 *   usePageView({ trackOnRouteChange: true });
 *   return <Router />;
 * }
 * ```
 */
function usePageView(props = {}) {
    const { trackOnMount = true, trackOnRouteChange = false } = props;
    const { pageView, loading } = usePixel();
    const previousUrlRef = useRef('');
    const loadingRef = useRef(loading);
    loadingRef.current = loading;
    useEffect(() => {
        if (trackOnMount && !loading) {
            pageView();
        }
    }, [trackOnMount, loading, pageView]);
    useEffect(() => {
        if (!trackOnRouteChange || typeof window === 'undefined')
            return;
        previousUrlRef.current = window.location.href;
        function handleUrlChange() {
            const currentUrl = window.location.href;
            if (previousUrlRef.current !== currentUrl) {
                previousUrlRef.current = currentUrl;
                if (!loadingRef.current) {
                    pageView();
                }
            }
        }
        ensureHistoryPatched();
        window.addEventListener('popstate', handleUrlChange);
        window.addEventListener(HISTORY_CHANGE_EVENT, handleUrlChange);
        return () => {
            window.removeEventListener('popstate', handleUrlChange);
            window.removeEventListener(HISTORY_CHANGE_EVENT, handleUrlChange);
        };
    }, [trackOnRouteChange, pageView]);
    return { pageView, loading };
}

/**
 * Hook for making Facebook Graph API calls with automatic loading/error state management
 *
 * @param options - Configuration for the Graph API call
 * @returns Object with data, loading state, error, and fetch/reset functions
 *
 * @example
 * ```tsx
 * // Auto-fetch on mount
 * function MyFriends() {
 *   const { data, loading, error } = useGraphAPI({
 *     path: '/me/friends',
 *     params: { limit: 10 },
 *   });
 *
 *   if (loading) return <p>Loading...</p>;
 *   if (error) return <p>Error: {error.message}</p>;
 *   return <ul>{data?.data?.map(f => <li key={f.id}>{f.name}</li>)}</ul>;
 * }
 *
 * // Manual fetch
 * function PostComment() {
 *   const { fetch, loading } = useGraphAPI({
 *     path: '/me/feed',
 *     method: Method.POST,
 *     autoFetch: false,
 *   });
 *
 *   return (
 *     <button onClick={() => fetch({ message: 'Hello!' })} disabled={loading}>
 *       Post
 *     </button>
 *   );
 * }
 * ```
 */
function useGraphAPI(options) {
    const { path, method = Method.GET, params, autoFetch = true, transform } = options;
    const { init } = useFacebook();
    const [data, setData] = useState(undefined);
    const [loading, setLoading] = useState(autoFetch);
    const [error, setError] = useState(undefined);
    const mountedRef = useRef(true);
    const transformRef = useRef(transform);
    transformRef.current = transform;
    // Stabilize params for dependency tracking
    const paramsKey = params ? JSON.stringify(params) : '';
    const fetchData = useCallback(async (overrideParams) => {
        var _a;
        try {
            setError(undefined);
            setLoading(true);
            const api = await init();
            if (!api) {
                throw new Error('[react-facebook] Facebook API is not initialized');
            }
            const requestParams = (_a = overrideParams !== null && overrideParams !== void 0 ? overrideParams : params) !== null && _a !== void 0 ? _a : {};
            const response = await api.api(path, method, requestParams);
            const result = transformRef.current ? transformRef.current(response) : response;
            if (mountedRef.current) {
                setData(result);
            }
            return result;
        }
        catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            if (mountedRef.current) {
                setError(error);
            }
            throw error;
        }
        finally {
            if (mountedRef.current) {
                setLoading(false);
            }
        }
    }, [init, path, method, paramsKey]);
    const reset = useCallback(() => {
        setData(undefined);
        setError(undefined);
        setLoading(false);
    }, []);
    useEffect(() => {
        mountedRef.current = true;
        if (autoFetch) {
            fetchData().catch(() => {
                // Error is already stored in state
            });
        }
        return () => {
            mountedRef.current = false;
        };
    }, [autoFetch, fetchData]);
    return { data, loading, error, fetch: fetchData, reset };
}

/**
 * Standalone Facebook Pixel API — no provider required.
 *
 * Drop-in replacement for `react-facebook-pixel`:
 *
 * @example
 * ```ts
 * import { ReactPixel } from 'react-facebook';
 *
 * ReactPixel.init('YOUR_PIXEL_ID');
 * ReactPixel.pageView();
 * ReactPixel.track('Purchase', { value: 29.99, currency: 'USD' });
 * ```
 */
let instance;
function getInstance() {
    if (!instance) {
        throw new Error('[react-facebook] ReactPixel.init() must be called before using any tracking methods.');
    }
    return instance;
}
const ReactPixel = {
    /**
     * Initialize Facebook Pixel.
     *
     * @param pixelId - Your Facebook Pixel ID
     * @param advancedMatching - Optional advanced matching parameters (e.g. `{ em: 'user@example.com' }`)
     * @param options - Optional configuration (`autoConfig`, `debug`)
     */
    init(pixelId, advancedMatching, options) {
        instance = createFacebookPixel({
            pixelId,
            advancedMatching,
            ...options,
        });
        return instance.init().then(() => undefined);
    },
    /** Track a page view event. */
    pageView() {
        return getInstance().pageView();
    },
    /** Track a standard Facebook event. */
    track(eventName, data) {
        return getInstance().track(eventName, data);
    },
    /** Track a standard event for a specific pixel. */
    trackSingle(pixelId, eventName, data) {
        return getInstance().trackSingle(pixelId, eventName, data);
    },
    /** Track a custom event. */
    trackCustom(eventName, data) {
        return getInstance().trackCustom(eventName, data);
    },
    /** Track a custom event for a specific pixel. */
    trackSingleCustom(pixelId, eventName, data) {
        return getInstance().trackSingleCustom(pixelId, eventName, data);
    },
    /** Grant GDPR tracking consent. */
    grantConsent() {
        return getInstance().grantConsent();
    },
    /** Revoke GDPR tracking consent. */
    revokeConsent() {
        return getInstance().revokeConsent();
    },
    /** Direct access to fbq for advanced use cases. */
    fbq(...args) {
        return getInstance().fbq(...args);
    },
};

export { ColorScheme_default as ColorScheme, Comments_default as Comments, CommentsCount_default as CommentsCount, CommentsOrderBy_default as CommentsOrderBy, EmbeddedPost_default as EmbeddedPost, EmbeddedVideo_default as EmbeddedVideo, FBError, FacebookContext, FacebookErrorBoundary, FacebookPixelContext, FacebookPixelProvider, FacebookProvider, Fields, Like_default as Like, LikeAction_default as LikeAction, LikeLayout_default as LikeLayout, LikeSize_default as LikeSize, Login, LoginStatus, MessengerColor_default as MessengerColor, MessengerSize_default as MessengerSize, Page_default as Page, Parser, ReactPixel, Share_default as Share, ShareButton, useFacebook, useFeed, useGraphAPI, useLocale, useLogin, useLoginStatus, usePageView, usePixel, useProfile, useSend, useShare, useSubscribe };
//# sourceMappingURL=index.js.map
